import { useUploadStore } from "@components/FileUpload/uploadStore";
import { useNotification } from "@context/NotificationContext";
import { validateVesselCertificates } from "@features/vessel-call/create/utils/validateCertificates.ts";
import { VESSEL_CALL_STATUSES } from "@features/vessel-call/vessel-call-status/flow/utils.tsx";
import { mapMovementFormDataToPayload } from "@features/vessel-call/vessel-call-status/MovementServices/form/utils.ts";
import { getMovementServiceFormSchema } from "@features/vessel-call/vessel-call-status/MovementServices/schema/movementServiceFormSchema.ts";
import type { MovementServiceFormData } from "@features/vessel-call/vessel-call-status/MovementServices/schema/movementServiceFormSchema.ts";
import { useMovementServiceFormStore } from "@features/vessel-call/vessel-call-status/MovementServices/store/useMovementServiceForm.store";
import { SHIPPING_AGENCY_TRANSFER_STATUS } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils";
import { useAuthStore } from "@persisted-state/auth/auth.store.ts";
import type { MovementRequestPayload } from "@services/movement-services/types";
import { submitMovementRequest } from "@services/movementServicesApi.ts";
import { getLatestShippingAgencyTransfer } from "@services/shippingAgencyTransferApi";
import type { Vessel } from "@services/vessel/types.ts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { cleanVesselCallPayload, removeNullValueKeys } from "@utils/payloadClean.ts";
import { t } from "i18next";
import { useNavigate } from "react-router-dom";
import { z } from "zod";

interface UseMovementServiceProps {
  formValues: z.infer<ReturnType<typeof getMovementServiceFormSchema>>;
  resetForm?: () => void;
  onSettled?: () => void;
  redirectPath?: string;
  movementId?: string;
  vesselId?: string;
  vesselCallId?: string;
  vesselCallStatus?: string;
  vesselRegisterDetails?: Vessel | null;
}

type AdditionalDocumentEntry = NonNullable<MovementServiceFormData["additionalDocuments"]>[number];

type ApiErrorLike = {
  response?: {
    data?: {
      detail?: string;
    };
  };
  message?: string;
};

const hasDateValue = (value: unknown) => {
  if (value == null) return false;
  if (typeof value === "string") return value.trim().length > 0;
  return true;
};

const pickNonEmpty = <T,>(...values: Array<T | undefined | null>): T | undefined => {
  for (const value of values) {
    if (typeof value === "string") {
      if (value.trim().length > 0) return value as T;
      continue;
    }
    if (value != null) return value as T;
  }
  return undefined;
};

const pickDateOrNull = (
  submittedDate: AdditionalDocumentEntry["issueDate"] | AdditionalDocumentEntry["expiryDate"],
  storeDate: AdditionalDocumentEntry["issueDate"] | AdditionalDocumentEntry["expiryDate"],
  formDate: AdditionalDocumentEntry["issueDate"] | AdditionalDocumentEntry["expiryDate"],
) => {
  if (hasDateValue(submittedDate)) return submittedDate;
  if (hasDateValue(storeDate)) return storeDate;
  if (hasDateValue(formDate)) return formDate;
  return null;
};

const mergeAdditionalDocumentRow = (
  idx: number,
  submittedDoc: AdditionalDocumentEntry | undefined,
  storeDoc: AdditionalDocumentEntry | undefined,
  formDoc: AdditionalDocumentEntry | undefined,
): AdditionalDocumentEntry => {
  const documentType =
    pickNonEmpty(submittedDoc?.documentType, storeDoc?.documentType, formDoc?.documentType) ?? "";

  return {
    name: pickNonEmpty(submittedDoc?.name, storeDoc?.name, formDoc?.name) ?? "",
    url: pickNonEmpty(submittedDoc?.url, storeDoc?.url, formDoc?.url) ?? "",
    certificateType:
      pickNonEmpty(
        submittedDoc?.certificateType,
        storeDoc?.certificateType,
        formDoc?.certificateType,
      ) ?? "",
    idx: pickNonEmpty<number>(submittedDoc?.idx, storeDoc?.idx, formDoc?.idx) ?? idx,
    type: pickNonEmpty(submittedDoc?.type, storeDoc?.type, formDoc?.type),
    documentType,
    otherSpecifyType:
      documentType === "OTHER_SPECIFY"
        ? (pickNonEmpty(
            submittedDoc?.otherSpecifyType,
            storeDoc?.otherSpecifyType,
            formDoc?.otherSpecifyType,
          ) ?? "")
        : "",
    documentName:
      pickNonEmpty(submittedDoc?.documentName, storeDoc?.documentName, formDoc?.documentName) ?? "",
    issueDate: pickDateOrNull(submittedDoc?.issueDate, storeDoc?.issueDate, formDoc?.issueDate),
    expiryDate: pickDateOrNull(submittedDoc?.expiryDate, storeDoc?.expiryDate, formDoc?.expiryDate),
    documentSummary:
      pickNonEmpty(
        submittedDoc?.documentSummary,
        storeDoc?.documentSummary,
        formDoc?.documentSummary,
      ) ?? "",
  };
};

const mergeAdditionalDocuments = (
  submittedDocs: MovementServiceFormData["additionalDocuments"] | undefined,
  storeDocs: MovementServiceFormData["additionalDocuments"] | undefined,
  formDocs: MovementServiceFormData["additionalDocuments"] | undefined,
): MovementServiceFormData["additionalDocuments"] => {
  const submitted = submittedDocs ?? [];
  const store = storeDocs ?? [];
  const form = formDocs ?? [];

  if (submitted.length === 0 && store.length === 0 && form.length === 0) {
    return undefined;
  }

  const getDocumentIdentityKey = (
    doc: AdditionalDocumentEntry | undefined,
    idx: number,
  ): string => {
    const url = doc?.url?.trim();
    if (url) return `url:${url}`;

    const name = doc?.name?.trim();
    if (name) return `name:${name}`;

    return `idx:${idx}`;
  };

  const toIdentityMap = (
    docs: MovementServiceFormData["additionalDocuments"],
  ): Map<string, AdditionalDocumentEntry> => {
    const map = new Map<string, AdditionalDocumentEntry>();
    (docs ?? []).forEach((doc, idx) => {
      if (!doc) return;
      map.set(getDocumentIdentityKey(doc, idx), doc);
    });
    return map;
  };

  const storeByKey = toIdentityMap(store);
  const formByKey = toIdentityMap(form);
  const submittedByKey = toIdentityMap(submitted);

  const orderedKeys = new Set<string>();
  store.forEach((doc, idx) => {
    if (!doc) return;
    orderedKeys.add(getDocumentIdentityKey(doc, idx));
  });
  form.forEach((doc, idx) => {
    if (!doc) return;
    orderedKeys.add(getDocumentIdentityKey(doc, idx));
  });
  submitted.forEach((doc, idx) => {
    if (!doc) return;
    orderedKeys.add(getDocumentIdentityKey(doc, idx));
  });

  const merged: AdditionalDocumentEntry[] = [];

  Array.from(orderedKeys).forEach((key, idx) => {
    const submittedDoc = submittedByKey.get(key);
    const storeDoc = storeByKey.get(key);
    const formDoc = formByKey.get(key);

    if (!submittedDoc && !storeDoc && !formDoc) return;

    merged.push(mergeAdditionalDocumentRow(idx, submittedDoc, storeDoc, formDoc));
  });

  return merged.length ? merged : undefined;
};

export interface UseMovementServiceResult {
  onSubmitMovementService: (submittedFormValues?: MovementServiceFormData) => Promise<void>;
}

const LANG_BASE_TRANSFER = "vessel-call.shipping-agency-transfer.notifications";

export const useMovementService = (props: UseMovementServiceProps): UseMovementServiceResult => {
  const {
    resetForm,
    onSettled,
    formValues,
    movementId,
    vesselCallId,
    vesselId,
    vesselRegisterDetails,
    vesselCallStatus,
  } = props;

  const { notify } = useNotification();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const clearUploadStore = useUploadStore((state) => state.clearAll);

  const { data: latestTransfer } = useQuery({
    queryKey: ["getLatestShippingAgencyTransfer", vesselCallId],
    queryFn: () =>
      getLatestShippingAgencyTransfer("bff-vessel-call", {
        vesselCallId: vesselCallId ?? "",
      }),
    enabled: !!vesselCallId,
    staleTime: 0,
    refetchOnMount: true,
  });

  const mutation = useMutation<unknown, unknown, MovementRequestPayload>({
    mutationFn: (payload: MovementRequestPayload) => {
      const isResubmission = useAuthStore.getState().movementServiceResubmit ?? undefined;
      return runTransaction(
        {
          module: "vessel_call",
          screen: "MovementService",
          action: isResubmission ? "resubmit_movement_service" : "submit_movement_service",
          entityType: "movement_service",
        },
        (transactionId) =>
          submitMovementRequest({
            service: "bff-vessel-call",
            payload,
            isResubmission,
            movementId,
            ...(transactionId ? { transactionId } : {}),
          }),
      );
    },
    onSuccess: () => {
      setTimeout(async () => {
        await queryClient.invalidateQueries({
          queryKey: ["getCurrentMovementService", vesselCallId, vesselId],
        });
        onSettled?.();
      }, 2000);

      if (movementId) {
        setTimeout(async () => {
          await queryClient.invalidateQueries({
            queryKey: ["movement-service-preview", movementId],
          });
        }, 2000);
      }
      resetForm?.();
      navigate(-1);
      clearUploadStore();

      notify({
        title: t("vessel-call.movement-services.notifications.request-success-title"),
        description: t("vessel-call.movement-services.notifications.request-success-description"),
        type: "success",
      });
    },
    onError: (error: unknown) => {
      const normalizedError = error as ApiErrorLike;
      onSettled?.();
      notify({
        title: t("vessel-call.movement-services.notifications.request-error-title"),
        description:
          normalizedError?.response?.data?.detail ??
          normalizedError?.message ??
          t("vessel-call.movement-services.notifications.request-error-description"),
        type: "error",
      });
    },
  });

  const onSubmitMovementService = async (
    submittedFormValues?: MovementServiceFormData,
  ): Promise<void> => {
    const isVesselCallClosed = vesselCallStatus === VESSEL_CALL_STATUSES.CLOSED;
    if (isVesselCallClosed) {
      notify({
        title: t("vessel-call.movement-services.notifications.movement-create-error-title"),
        description: t("vessel-call.movement-services.notifications.vessel-call-closed"),
        type: "error",
      });
      onSettled?.();
      return;
    }
    const storeFormValues = useMovementServiceFormStore.getState();
    const mergedFormValues = submittedFormValues
      ? { ...formValues, ...storeFormValues, ...submittedFormValues }
      : { ...formValues, ...storeFormValues };

    const latestFormValues = {
      ...mergedFormValues,
      additionalDocuments: mergeAdditionalDocuments(
        submittedFormValues?.additionalDocuments,
        storeFormValues.additionalDocuments,
        formValues.additionalDocuments,
      ),
      // Keep required identifiers from store/props when submit payload carries empty defaults.
      from: mergedFormValues.from || storeFormValues.from || formValues.from,
      vesselId: mergedFormValues.vesselId || vesselId || storeFormValues.vesselId,
      vesselCallId: mergedFormValues.vesselCallId || vesselCallId || storeFormValues.vesselCallId,
    };

    const hasPendingTransfer =
      latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_APPROVAL ||
      latestTransfer?.status === SHIPPING_AGENCY_TRANSFER_STATUS.PENDING_MARINE_DEPARTMENT_APPROVAL;

    const invalidCertificates = validateVesselCertificates(
      latestFormValues.to,
      vesselRegisterDetails,
      vesselRegisterDetails?.diplomaticVessel,
    );

    if (invalidCertificates.length > 0) {
      const certList = invalidCertificates
        .map((cert) => `- ${t(`vessel-management.certificates.${cert}`)}`)
        .join("\n");

      notify({
        title: t("vessel-call.create.notifications.certificate-validation-title"),
        description: `${t("vessel-call.create.notifications.certificate-validation-description-for-movement")}\n \n${certList}`,
        type: "error",
        autoHideDuration: 60 * 1000,
      });
      onSettled?.();
      return; //submission blocked due to expired/missing certificates
    }

    if (hasPendingTransfer) {
      notify({
        title: t("vessel-call.movement-services.notifications.movement-create-error-title"),
        description: t(`${LANG_BASE_TRANSFER}.active-shipping-agency-transfer-error`),
        type: "error",
      });
      onSettled?.();
      return;
    } else {
      const mappedValueForPayload = mapMovementFormDataToPayload(latestFormValues);
      const cleanPayload = cleanVesselCallPayload(mappedValueForPayload);
      const payloadWithoutNulls = removeNullValueKeys(cleanPayload);

      mutation.mutate(payloadWithoutNulls);
    }
  };

  return { onSubmitMovementService };
};
